import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from "@/components/ui/tooltip";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { KeyBadge, KEY_OPTIONS, BEHEERDER_OPTIONS, KEY_NONE } from "@/components/KeyBadge";
import { KeyRound, Plus, Loader2, Pencil, Trash2, Search, Info } from "lucide-react";
import { formatDate, formatDateShort } from "@/lib/date";
import { toast } from "sonner";

import { tx, lz } from "@/i18n";
const empty = { woning: "", beheerder: "", set_c: "", set_g: "", set_g2: "", set_e: "", bijzonderheden: "" };
const COLS = lz([
  { key: "set_c", label: "Set C", short: "C" },
  { key: "set_g", label: "Set G", short: "G" },
  { key: "set_g2", label: "2e Set G", short: "2G" },
  { key: "set_e", label: "Set E", short: "E" },
]);

const fmtDate = (iso) => formatDate(iso) || "—";
const fmtDateShort = (iso) => formatDateShort(iso) || "—";

export default function KeyManagement() {
  const { user } = useAuth();
  const [rows, setRows] = useState(null);
  const [q, setQ] = useState("");
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const hasAccess = user.role === "admin" || user.key_access;
  const load = () => api.get("/keys").then(({ data }) => setRows(data)).catch(() => setRows([]));
  useEffect(() => { if (hasAccess) load(); }, [hasAccess]);

  if (!hasAccess) return <Navigate to="/app" replace />;

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const openNew = () => { setForm(empty); setEditing({ id: null }); };
  const openEdit = (r) => { setForm({ ...empty, ...r }); setEditing(r); };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { woning: form.woning, beheerder: form.beheerder, set_c: form.set_c, set_g: form.set_g, set_g2: form.set_g2, set_e: form.set_e, bijzonderheden: form.bijzonderheden };
      if (editing.id) await api.put(`/keys/${editing.id}`, payload);
      else await api.post("/keys", payload);
      toast.success(editing.id ? tx("Sleutelrij bijgewerkt") : tx("Sleutelrij toegevoegd"));
      setEditing(null); load();
    } catch (err) { toast.error(apiError(err)); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm(tx("Deze sleutelrij verwijderen?"))) return;
    try { await api.delete(`/keys/${id}`); toast.success(tx("Verwijderd")); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  const filtered = (rows || []).filter((r) =>
    !q || [r.woning, r.beheerder, r.set_c, r.set_g, r.set_g2, r.set_e, r.bijzonderheden]
      .some((v) => (v || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="animate-fade-up">
      <div className="flex items-center justify-between mb-8 gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl tracking-tight">{tx("Sleutelbeheer")}</h1>
          <p className="text-muted-foreground mt-1">{tx("Wie heeft welke sleutelset per woning.")}</p>
        </div>
        {user.role === "admin" && (
          <Button onClick={openNew} className="bg-primary hover:bg-primary/90 gap-2" data-testid="add-key-btn">
            <Plus className="h-4 w-4" /> {tx("Nieuwe rij")}
          </Button>
        )}
      </div>

      <div className="relative mb-4 max-w-sm">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={tx("Zoek op woning, beheerder…")} className="pl-9" data-testid="key-search" />
      </div>

      {!rows ? (
        <div className="flex justify-center py-20"><Loader2 className="h-7 w-7 animate-spin text-primary" /></div>
      ) : filtered.length === 0 ? (
        <div className="bg-card border border-border rounded-xl p-12 text-center text-muted-foreground">
          <KeyRound className="h-10 w-10 mx-auto mb-3 opacity-40" /> {tx("Geen sleutelrijen gevonden.")}
        </div>
      ) : (
        <div className="bg-card rounded-xl border border-border shadow-sm overflow-x-auto">
          <table className="w-full text-[11px] sm:text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left">
                <th className="px-1 py-2 sm:px-4 sm:py-3 font-display font-semibold">{tx("Woning")}</th>
                <th className="px-1 py-2 sm:px-4 sm:py-3 font-display font-semibold"><span className="sm:hidden">{tx("Beh.")}</span><span className="hidden sm:inline">{tx("Beheerder")}</span></th>
                {COLS.map((c) => <th key={c.key} className="px-1 py-2 sm:px-4 sm:py-3 font-display font-semibold text-center sm:text-left"><span className="sm:hidden">{c.short}</span><span className="hidden sm:inline">{c.label}</span></th>)}
                <th className="hidden sm:table-cell px-1 py-2 sm:px-4 sm:py-3 font-display font-semibold">{tx("Aangepast")}</th>
                <th className="px-1 py-2 sm:px-4 sm:py-3 font-display font-semibold text-center sm:text-left"><span className="hidden sm:inline">{tx("Bijzonderheden")}</span></th>
                <th className="px-1 py-2 sm:px-4 sm:py-3"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r) => (
                <tr key={r.id} className="border-b border-border last:border-0 hover:bg-accent/30 align-top" data-testid={`key-row-${r.id}`}>
                  <td className="px-1 py-2 sm:px-4 sm:py-3 font-semibold whitespace-normal sm:whitespace-nowrap break-words leading-tight">{r.woning}</td>
                  <td className="px-1 py-2 sm:px-4 sm:py-3 whitespace-nowrap"><KeyBadge value={r.beheerder} plain /></td>
                  {COLS.map((c) => (
                    <td key={c.key} className="px-1 py-2 sm:px-4 sm:py-3 whitespace-nowrap text-center sm:text-left"><KeyBadge value={r[c.key]} /></td>
                  ))}
                  <td className="hidden sm:table-cell px-1 py-2 sm:px-4 sm:py-3 whitespace-nowrap text-muted-foreground">{fmtDate(r.datum_aangepast)}</td>
                  <td className="px-1 py-2 sm:px-4 sm:py-3 sm:max-w-[280px] text-muted-foreground text-center sm:text-left">
                    {r.bijzonderheden ? (
                      <>
                        <span className="hidden sm:inline">{r.bijzonderheden}</span>
                        <Popover>
                          <PopoverTrigger asChild>
                            <button className="sm:hidden inline-flex text-primary" data-testid={`key-note-${r.id}`}><Info className="h-4 w-4" /></button>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="max-w-[240px] text-xs text-left">{r.bijzonderheden}</PopoverContent>
                        </Popover>
                      </>
                    ) : ""}
                  </td>
                  <td className="px-1 py-2 sm:px-4 sm:py-3">
                    <div className="flex flex-col items-end gap-0.5 sm:flex-row sm:gap-1 sm:justify-end">
                      <span className="sm:hidden text-[10px] leading-none text-muted-foreground whitespace-nowrap" data-testid={`key-date-m-${r.id}`}>{fmtDateShort(r.datum_aangepast)}</span>
                      <div className="flex items-center gap-0.5 sm:gap-1">
                        <Button size="icon" variant="ghost" onClick={() => openEdit(r)} className="h-7 w-7 sm:h-8 sm:w-8" data-testid={`edit-key-${r.id}`}><Pencil className="h-3.5 w-3.5 sm:h-4 sm:w-4" /></Button>
                        {user.role === "admin" && (
                          <Button size="icon" variant="ghost" onClick={() => del(r.id)} className="h-7 w-7 sm:h-8 sm:w-8 text-destructive" data-testid={`delete-key-${r.id}`}><Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" /></Button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto" data-testid="key-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">{editing?.id ? tx("Sleuteloverdracht registreren") : tx("Nieuwe sleutelrij")}</DialogTitle>
            <DialogDescription>{tx("De datum \"Aangepast\" wordt automatisch bijgewerkt bij opslaan.")}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>{tx("Woning *")}</Label><Input value={form.woning} onChange={set("woning")} disabled={user.role !== "admin"} className="mt-1" data-testid="key-woning-input" /></div>
            <div>
              <Label>{tx("Beheerder")}</Label>
              <Select value={form.beheerder || KEY_NONE} onValueChange={(v) => setForm({ ...form, beheerder: v === KEY_NONE ? "" : v })} disabled={user.role !== "admin"}>
                <SelectTrigger className="mt-1" data-testid="key-beheerder-select"><SelectValue placeholder={tx("Kies…")} /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={KEY_NONE}>{tx("— leeg —")}</SelectItem>
                  {BEHEERDER_OPTIONS.map((o) => <SelectItem key={o} value={o}><KeyBadge value={o} /></SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {[["set_c", tx("Set C")], ["set_g", tx("Set G")], ["set_g2", tx("2e Set G")], ["set_e", tx("Set E")]].map(([field, label]) => (
                <div key={field}>
                  <Label>{label}</Label>
                  <Select value={form[field] || KEY_NONE} onValueChange={(v) => setForm({ ...form, [field]: v === KEY_NONE ? "" : v })}>
                    <SelectTrigger className="mt-1" data-testid={`key-${field}-select`}><SelectValue placeholder={tx("Kies…")} /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={KEY_NONE}>{tx("— leeg —")}</SelectItem>
                      {KEY_OPTIONS.map((o) => <SelectItem key={o} value={o}><KeyBadge value={o} /></SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <div><Label>{tx("Bijzonderheden")}</Label><Textarea value={form.bijzonderheden} onChange={set("bijzonderheden")} className="mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>{tx("Annuleer")}</Button>
            <Button onClick={save} disabled={saving || !form.woning} className="bg-primary hover:bg-primary/90 gap-2" data-testid="save-key-btn">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {tx("Opslaan")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
